import { cn } from "@ui/lib/utils";

type FadeDirection = "bottom" | "none" | "top" | boolean;

type DottedBorderColor = "dark" | "light" | (string & {});

type StringPixelValue = `${number}px`;

type DottedBorderProps = {
  className?: string;
  color?: DottedBorderColor; // "light" = 15%, "dark" = 60%, or custom
  dotSize?: StringPixelValue; // e.g. "1px"
  fade?: FadeDirection;
  gap?: StringPixelValue; // e.g. "6px"
  side: "bottom" | "inline" | "left" | "right" | "top" | "vertical";
  thickness?: StringPixelValue; // e.g. "2px"
};

const createBorderStyles = (
  side: "bottom" | "inline" | "left" | "right" | "top" | "vertical",
  thickness: StringPixelValue,
  colorValue: string,
  dotSize: StringPixelValue,
  gap: StringPixelValue,
  mask?: string,
) => {
  if (side === "top" || side === "bottom" || side === "inline") {
    return {
      position: "absolute" as const,
      left: 0,
      ...(side !== "inline" && { [side]: 0 }),
      width: "100%",
      height: thickness,
      backgroundImage: `repeating-radial-gradient(circle,${colorValue} 0 ${dotSize},transparent 1px ${gap})`,
      backgroundSize: `${gap} 100%`,
      opacity: 1,
    };
  } else if (side === "vertical") {
    return {
      position: "absolute" as const,
      top: 0,
      left: "50%",
      transform: "translateX(-50%)",
      width: thickness,
      height: "100%",
      backgroundImage: `repeating-radial-gradient(circle,${colorValue} 0 ${dotSize},transparent 1px ${gap})`,
      backgroundSize: `100% ${gap}`,
      opacity: 1,
      maskImage: mask,
      WebkitMaskImage: mask,
    };
  } else {
    return {
      position: "absolute" as const,
      top: 0,
      [side]: 0,
      width: thickness,
      height: "100%",
      backgroundImage: `repeating-radial-gradient(circle,${colorValue} 0 ${dotSize},transparent 1px ${gap})`,
      backgroundSize: `100% ${gap}`,
      opacity: 1,
      maskImage: mask,
      WebkitMaskImage: mask,
    };
  }
};

export const DottedBorder = ({
  side,
  className,
  color = "light",
  dotSize = "1px",
  gap = "6px",
  thickness = "2px",
  fade = false,
}: DottedBorderProps) => {
  // Determine color value
  let colorValue: string;
  if (color === "light") {
    colorValue = "rgba(0,0,0,0.15)";
  } else if (color === "dark") {
    colorValue = "rgba(0,0,0,0.6)";
  } else {
    colorValue = color; // custom color string
  }

  // Only apply fade for left/right sides
  let mask: string | undefined;
  if (side === "left" || side === "right" || side === "vertical") {
    if (fade === "top") {
      // Solid at bottom, transparent at top
      mask = "linear-gradient(to top, black 0%, black 20%, transparent 100%)";
    } else if (fade === "bottom") {
      // Solid at top, transparent at bottom
      mask = "linear-gradient(to bottom, black 0%, black 20%, transparent 100%)";
    } else if (fade === true) {
      mask = "linear-gradient(to bottom, transparent 0%, black 20%, black 80%, transparent 100%)";
    }
    // fade === false or "none": no mask
  }
  // For top/bottom, mask is always undefined (no fade)

  const base = createBorderStyles(side, thickness, colorValue, dotSize, gap, mask);

  const line = <div aria-hidden className={cn(className)} style={base} />;

  if (side === "inline") {
    return <div className='relative h-[2px]'>{line}</div>;
  }

  return line;
};

// Example usage for top and bottom borders (no fade)
export const GradientBorderToTop = ({
  className,
  color = "light",
}: {
  className?: string;
  color?: DottedBorderColor;
}) => (
  <div className='relative h-[2px] w-full'>
    <DottedBorder className={className} color={color} side='top' />
  </div>
);

export const GradientBorderToBottom = ({
  className,
  color = "light",
}: {
  className?: string;
  color?: DottedBorderColor;
}) => (
  <div className='relative h-[2px] w-full'>
    <DottedBorder className={className} color={color} side='bottom' />
  </div>
);
